<template>
  <div class="new-report-form"
       :class="companyType">
    <div class="new-report-form-wrap">
      <div class="diagnosing"
           v-if="diagnosingId">
        <h2>
          {{ $t('diagnostics.diagnosing') }}...
          <button @click="onCancelClick">Cancel</button>
        </h2>

        <div class="progress-bar">
          <div class="bar"
               :class="{ on: progressWhat !== 'waiting' }"
               :style="{ width: progressShowPercent + '%' }">
            <i class="icon icon--rocket"></i>
            <span class="bubble">{{ Math.round(progressShowPercent) }}%</span>
          </div>
        </div>

        <div class="progress-what">
          <div class="what"
               :class="{ active: progressWhat === 'waiting' }">
            <i class="icon icon--check-white"></i>
            <h5>Waiting</h5>
            <p>There are {{ queueCount }} in the queue.</p>
          </div>
          <div class="what"
               :class="{ active: progressWhat === 'rendering' }">
            <i class="icon icon--check-white"></i>
            <h5>Rendering</h5>
            <p></p>
          </div>
          <div class="what"
               :class="{ active: ['evaluating', 'ocr'].includes(progressWhat) }">
            <i class="icon icon--check-white"></i>
            <h5>
              Evaluating
              <!--<span :class="{ ac: progressWhat === 'ocr' }">(OCR)</span>-->
            </h5>
            <p></p>
          </div>
          <div class="what"
               :class="{ active: progressWhat === 'done' }">
            <i class="icon icon--check-white"></i>
            <h5>Done</h5>
            <p></p>
          </div>
        </div>
      </div>

      <template v-else>
        <h2>Diagnosis</h2>

        <div class="make-diagnosis-tab">
          <ul>
            <li>
              <a href="#"
                 :class="{ active: diagnosisType === 'authoring' }"
                 @click.prevent="$emit('onShowDiagnosisClick', 'authoring')">
                Authoring page
              </a>
            </li>
            <li>
              <a href="#"
                 :class="{ active: diagnosisType === 'rawcode' }"
                 @click.prevent="$emit('onShowDiagnosisClick', 'rawcode')">
                Rawcode page
              </a>
            </li>
            <li>
              <a href="#"
                 :class="{ active: diagnosisType === 'image' }"
                 @click.prevent="$emit('onShowDiagnosisClick', 'image')">
                Image
              </a>
            </li>
          </ul>
        </div>

        <template v-if="diagnosisType === 'image'">
          <div class="file-options">
              <div class="inputs">
              <div class="file-input">
                <div class="label">
                  <div class="current">Copy Deck File</div>
                </div>
                <div class="file">
                  <div class="button"
                       :class="{ has: fileName }">
                    <button>Select File</button>
                    {{ fileName || 'or Drop File Here' }}
                  </div>
                  <input type="file"
                         id="diagnosisFile"
                         @change="onFileGetClick" />
                </div>
              </div>

              <div class="file-start">
                <button type="button"
                        @click="onFileStartClick"
                        :disabled="!canFileSubmit">Start</button>
              </div>
            </div>

            <div class="file-template"
                 v-if="!xlsxData">
              <a href="/static/WAQA-IMAGE(template).xlsx" download>Template Download</a>
            </div>

            <div class="file-items"
                 v-if="!!xlsxData">
              <table class="file-data">
                <thead>
                <tr>
                  <th>No.</th>
                  <th>Image</th>
                  <th>Copy Deck</th>
                </tr>
                </thead>
                <tbody>
                <tr v-for="(item, idx) in xlsxData"
                    :key="`xlsx${idx}`">
                  <td class="file-item-idx">
                    {{ idx + 1 }}.
                  </td>
                  <td class="file-item-image">
                    <div class="file-item-wrap"
                         :class="{ has: xlsxTempImageData[idx] }">
                      <img v-if="xlsxTempImageData[idx]"
                           :src="xlsxTempImageData[idx]"
                           alt="" />
                      <button>Select File</button>
                      <input type="file"
                             :id="`diagnosisItem${idx}`"
                             @change="setPreviewFile($event, idx)" />
                    </div>
                  </td>

                  <td class="file-item-attr">
                    <table>
                      <tr v-for="col in xlsxColumn"
                          :key="`xlsx${idx}${col}`">
                        <th>{{ col }}</th>
                        <td>{{ item[col] }}</td>
                      </tr>
                    </table>
                  </td>
                </tr>
                </tbody>
              </table>

              <div class="file-start">
                <button type="button"
                        @click="onFileStartClick"
                        :disabled="!canFileSubmit">Start</button>
              </div>
            </div>
          </div>
        </template>

        <template v-else-if="diagnosisType === 'authoring' || diagnosisType === 'rawcode'">
          <div class="url-options">
            <div class="url-input">
              <div class="url">
                <input type="text"
                       id="testURL"
                       v-model.trim="model.url" ref="urlInput"
                       placeholder="Enter URL"
                       @input="onUrlInput(model, 'url')"
                       @keyup.enter="onUrlSubmit"/>
                <!--<strong class="wrong" v-if="errors.url">* {{ $t(errors.url) }}</strong>-->
              </div>
            </div>

            <div class="url-start">
              <button type="button"
                      @click="onStartClick"
                      :disabled="!canUrlSubmit">Start</button>
            </div>
          </div>

          <transition name="slide">
            <div class="advanced-options"
                 v-if="isShowOptions">
              <div class="option-group">
                <div class="option-title">
                  <input type="checkbox"
                         class="input-check"
                         id="useExcludeSelectors"
                         v-model="options.useExcludeSelectors"
                         @change="toggleUseExcludeSelectors">
                  <label class="input-label"
                         for="useExcludeSelectors">{{ $t('diagnostics.excludeElements')}}</label>
                  <strong class="input-error" v-if="errors.useExcludeSelectors">* {{ $t(errors.useExcludeSelectors) }}</strong>
                  <span class="input-info">{{ $t('diagnostics.excludeSelectors') }}</span>
                </div>
                <div class="sub-options"
                     :style="useExcludeSelectorsStyle">
                  <ul class="excluded-list"
                      v-if="excludedSelectors.length">
                    <li v-for="(item, index) in excludedSelectors"
                        :key="`excludedSelectors${index}`">
                      {{ item }}
                    </li>
                  </ul>
                  <ul class="exclude-list">
                    <li v-for="(preset, index) in selectorPreset"
                        :key="preset.label">
                      <label :for="'excludeList' + (index + 1)">
                        <input type="checkbox"
                               :id="'excludeList' + (index + 1)"
                               :disabled="!options.useExcludeSelectors"
                               v-model="preset.selected">{{ preset.label }}
                      </label>
                    </li>
                  </ul>
                  <ul class="exclude-more"
                      v-if="isShowExcludeSelectorsMore">
                    <li v-for="(item, index) in excludeSelectors"
                        :key="index">
                      <input type="text"
                             v-model.trim="item.value" />
                      <span class="query-selector-input"></span>
                      <button type="button"
                              @click="onExcludeSelectorRemove(index)"
                              :disabled="!options.useExcludeSelectors">×</button>
                    </li>
                  </ul>
                  <button class="add-btn"
                          type="button"
                          @click="onExcludeSelectorAdd"
                          :disabled="!options.useExcludeSelectors">
                    <i class="icon icon--plus"></i> Add
                  </button>
                </div>
              </div>
              <div class="option-group">
                <div class="option-title">
                  <input type="checkbox"
                         class="input-check"
                         id="useIncludeSelectors"
                         v-model="options.useIncludeSelectors"
                         @change="toggleUseIncludeSelectors">
                  <label class="input-label"
                         for="useIncludeSelectors">{{ $t('diagnostics.includeElements')}}</label>
                  <strong class="input-error" v-if="errors.useIncludeSelectors">* {{ $t(errors.useIncludeSelectors) }}</strong>
                  <span class="input-info">{{ $t('diagnostics.includeSelectors') }}</span>
                </div>
                <div class="sub-options"
                     :style="useIncludeSelectorsStyle">
                  <ul class="excluded-list"
                      v-if="includedSelectors.length">
                    <li v-for="(item, index) in includedSelectors"
                        :key="`includedSelectors${index}`">
                      {{ item }}
                    </li>
                  </ul>
                  <ul class="exclude-more"
                      v-if="isShowIncludeSelectorsMore">
                    <li v-for="(item, index) in includeSelectors"
                        :key="index">
                      <input type="text"
                             v-model.trim="item.value" />
                      <span class="query-selector-input"></span>
                      <button type="button"
                              @click="onIncludeSelectorRemove(index)"
                              :disabled="!options.useIncludeSelectors">×</button>
                    </li>
                  </ul>
                  <button class="add-btn"
                          type="button"
                          @click="onIncludeSelectorAdd"
                          :disabled="!options.useIncludeSelectors">
                    <i class="icon icon--plus"></i> Add
                  </button>
                </div>
              </div>
              <div class="option-group">
                <p class="option-title">
                  <input type="checkbox"
                         id="requireLogin"
                         class="input-check"
                         v-model="options.requireLogin"
                         @change="toggleRequireLogin">
                  <label class="input-label"
                         for="requireLogin">{{ $t('diagnostics.requireLogin') }}</label>
                  <strong class="input-error" v-if="errors.requireLogin">* {{ $t(errors.requireLogin) }}</strong>
                  <span class="input-info">{{ $t('diagnostics.enterLoginInfo') }}</span>
                </p>
                <div class="sub-options"
                     :style="requireLoginStyle">
                  <ul class="target">
                    <li>
                      <label>
                        <input type="radio"
                               value="p5"
                               v-model.trim="loginOption"
                               :disabled="!options.requireLogin" />
                        P5
                      </label>
                    </li>
                    <li>
                      <label>
                        <input type="radio"
                               value="p6"
                               v-model.trim="loginOption"
                               :disabled="!options.requireLogin" />
                        P6
                      </label>
                    </li>
                  </ul>

                  <ul class="login-options">
                    <li>
                      <label for="optionsPageUrl">Page URL</label>
                      <input id="optionsPageUrl"
                             type="text"
                             v-model.trim="options.loginUrl"
                             placeholder="Please enter the information"
                             @input="onUrlInput(options, 'loginUrl')"
                             :disabled="!options.requireLogin">
                    </li>
                    <li>
                      <label for="optionsPWselector">Password <br>Query Selector</label>
                      <input id="optionsPWselector"
                             type="text"
                             placeholder="Please enter the information"
                             v-model.trim="options.loginPasswordSelector"
                             :disabled="!options.requireLogin">
                      <span class="query-selector-input"></span>
                    </li>
                    <li>
                      <label for="optionsIdSelector">Id <br>Query Selector</label>
                      <input id="optionsIdSelector"
                             type="text"
                             placeholder="Please enter the information"
                             v-model.trim="options.loginIdSelector"
                             :disabled="!options.requireLogin">
                      <span class="query-selector-input"></span>
                    </li>
                    <li>
                      <label for="optionsPW">Password</label>
                      <input id="optionsPW"
                             type="password"
                             placeholder="Please enter the information"
                             v-model.trim="options.loginPassword"
                             :disabled="!options.requireLogin">
                    </li>
                    <li>
                      <label for="optionsID">Id</label>
                      <input id="optionsID"
                             type="text"
                             placeholder="Please enter the information"
                             v-model.trim="options.loginId"
                             :disabled="!options.requireLogin">
                    </li>
                    <li>
                      <label for="optionsBtnSelector">Login Button <br>Query Selector</label>
                      <input id="optionsBtnSelector"
                             type="text"
                             placeholder="Please enter the information"
                             v-model.trim="options.loginButtonSelector"
                             :disabled="!options.requireLogin">
                      <span class="query-selector-input"></span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </transition>

          <div class="toggle-advanced"
               :class="isShowOptions ? 'on' : ''">
            <button type="button"
                    @click="onOptionsToggle">
              <span :class="isShowOptions ? 'on' : ''">&#9660;</span>
              {{ $t('diagnostics.advancedOptions') }}
            </button>
          </div>

        </template>
      </template>
    </div>
  </div>
</template>

<script>
import _ from 'lodash'
import { mapActions, mapGetters, mapMutations } from 'vuex'
import { parseXlsx } from '@/lib/parse-file'
import { companyType } from '@/lib/utils'
import ProgressBar from '@/components/global/ProgressBar'
import tempReportApi from '@/api/temp-report'
import modalEvent from '@/lib/modal-event'
import ajaxHandlerMixin from '@/mixins/ajax-handler'

export default {
  name: 'NewReportForm',
  mixins: [ajaxHandlerMixin],
  components: {
    ProgressBar
  },
  props: ['reDiagnosisReportId', 'diagnosisType'],
  watch: {
    reDiagnosisReportId (id) {
      this.setDiagnosing(id)
    },
    showDiagnosisDevice (value) {
      let bodyListener = (e) => {
        let outside = true

        function composedPath (el) {
          let paths = []
          while (el) {
            paths.push(el)
            if (el.tagName === 'HTML') {
              paths.push(document)
              paths.push(window)
              return paths
            }
            el = el.parentElement
          }
        }

        const path = e.path || (e.composedPath && e.composedPath()) || composedPath(e.target)
        if (path) {
          for (const el of path) {
            if (el.id && el.id === 'diagnosisDeviceDropdown') {
              outside = false
            }
          }
        }
        if (outside) {
          this.showDiagnosisDevice = false
        }
      }

      if (value) {
        document.body.addEventListener('click', bodyListener, false)
      } else {
        document.body.removeEventListener('click', bodyListener)
      }
    },
    loginOption (value) {
      this.options.loginUrl = this.defaultLoginUrl[value]
    },
    progressPercent (current, prev) {
      if (current <= 0) {
        this.progressShowPercent = 0
      } else {
        this.progressShowPercent = Math.min(100, Math.max(prev, current))
      }
    }
  },
  data () {
    return {
      showDiagnosisDevice: false,
      progressData: null,
      progressDataCount: 1,
      progressDataOrder: 0,
      progressShowPercent: 0,
      queueCount: '-',
      errors: {},
      fileName: '',
      xlsxColumn: null,
      xlsxData: null,
      xlsxTempImageData: null,
      isShowOptions: false,
      isShowExcludeSelectorsMore: false,
      isShowIncludeSelectorsMore: false,
      selectorPreset: [
        {
          label: 'GNB',
          selectors: [
            '#header',
            '#typeAheadDomain',
            '#scene7domain',
            '#reviewUseYN',
            '#reevooUseYN',
            '#bvFlag',
            '#frontApiStageInfo',
            '#gnbRunmodeInfo',
            '.gnb'
          ],
          selected: true
        },
        {
          label: 'Footer',
          selectors: [
            '#footer',
            '.footer',
            'footer'
          ],
          selected: true
        },
        {
          label: 'sticky-GoToTop',
          selectors: [
            '#wrap > div.s-gotop-wrap.s-show'
          ],
          selected: false
        },
        {
          label: 'sticky-Feedback',
          selectors: [
            '#oo_tab'
          ],
          selected: false
        }
      ],
      excludeSelectors: [],
      includeSelectors: [],
      model: {
        wcagVersion: '2.1',
        url: null,
        deviceType: 1
      },
      defaultLoginUrl: {
        p5: 'https://qaweb.samsung.com/ve/test/test_mde/mde_landing041/mde_landing04/',
        p6: 'https://p6-qa.samsung.com/ve/test/test_mde/mde_landing041/mde_landing04/'
      },
      loginOption: 'p6',
      defaultLoginOptions: {
        loginUrl: '',
        loginId: 'dotcom_qa',
        loginPassword: '!@#qapass00',
        loginIdSelector: '#username',
        loginPasswordSelector: '#password',
        loginButtonSelector: '#submit-button'
      },
      options: {
        useExcludeSelectors: true,
        useIncludeSelectors: false,
        requireLogin: false,
        excludeSelectors: null,
        includeSelectors: null,
        loginUrl: null,
        loginId: null,
        loginPassword: null,
        loginIdSelector: null,
        loginPasswordSelector: null,
        loginButtonSelector: null
      }
    }
  },
  computed: {
    ...mapGetters(['reportGroupData', 'diagnosingId']),
    companyType () {
      return companyType
    },
    isStartButtonDisabled () {
      return !this.model.url || !this.model.deviceType ||
        this.reportGroupData.isEditMode
    },
    useExcludeSelectorsStyle () {
      return this.getDisabledStyle(this.options.useExcludeSelectors)
    },
    useIncludeSelectorsStyle () {
      return this.getDisabledStyle(this.options.useIncludeSelectors)
    },
    requireLoginStyle () {
      return this.getDisabledStyle(this.options.requireLogin)
    },
    canUrlSubmit () {
      return !this.diagnosingId && !this.isStartButtonDisabled &&
        !this.diagnosingId
    },
    canFileSubmit () {
      return !this.diagnosingId && !!this.xlsxData &&
        !this.diagnosingId
    },
    progressValues () {
      if (!this.progressData) {
        return null
      }
      return this.progressData.split('/')
    },
    percentAdded () {
      return 100 * this.progressDataOrder / this.progressDataCount
    },
    progressPercent () {
      if (this.progressWhat === 'waiting') {
        return 2 + this.percentAdded
      }
      if (this.progressWhat === 'rendering') {
        return 10 / this.progressDataCount + this.percentAdded
      }
      if (this.progressWhat === 'done') {
        return 100 / this.progressDataCount + this.percentAdded
      }

      let progressingValues = this.progressValues[0].split('|')
      const pv0 = parseInt(progressingValues[0])
      const pv1 = parseInt(progressingValues[1])
      const currentStep = Math.sqrt(1 - Math.pow(1 - pv0 / pv1, 5)) * 88
      const nextStep = Math.sqrt(1 - Math.pow(1 - (pv0 + 1) / pv1, 5)) * 88
      let stepGap = 0
      if (this.progressValues.length > 1) {
        const ocrValues = this.progressValues[1].split('|')
        stepGap = (nextStep - currentStep) / ocrValues[1] * ocrValues[0]
      }

      return (10 + (currentStep + stepGap) * 0.95) / this.progressDataCount + this.percentAdded
    },
    progressWhat () {
      if (!this.progressValues) {
        return 'waiting'
      }
      const progressing = this.progressValues[0]
      if (['rendering', 'done'].includes(progressing)) {
        return progressing
      }

      let progressingValues = this.progressValues[0].split('|')
      if (this.progressValues.length > 1) {
        progressingValues = this.progressValues[1].split('|')
      }

      return progressingValues[2]
    },
    excludedSelectors () {
      let selectors = _.compact(_.map(this.excludeSelectors, item => {
        return item.value.trim()
      }))

      this.selectorPreset.forEach(preset => {
        if (preset.selected) {
          selectors = selectors.concat(preset.selectors)
        }
      })

      return selectors
    },
    includedSelectors () {
      let selectors = _.compact(_.map(this.includeSelectors, item => {
        return item.value.trim()
      }))

      return selectors
    }
  },
  created () {
    this.checkDiagnosing()
  },
  mounted () {
    this.$refs.urlInput.focus()
  },
  beforeDestroy () {
    clearInterval(this.timer)
  },
  methods: {
    ...mapMutations(['setDiagnosingId']),
    ...mapActions(['fetchDiagnosingId']),

    checkDiagnosing () {
      return new Promise((resolve, reject) => {
        this.fetchDiagnosingId().then(data => {
          if (data && data.mainReport) {
            this.setDiagnosing(data.mainReport)
            reject(new Error(`report exist: ${data.mainReport}`))
          } else {
            resolve()
          }
        })
      })
    },

    setDiagnosing (id) {
      this.progressData = null
      this.queueCount = '-'
      this.setDiagnosingId(id)
      window.scroll({
        top: 0,
        left: 0,
        behavior: 'smooth'
      })
      tempReportApi.getQueue().then((d) => {
        this.queueCount = d.data
      })
      this.timer = setInterval(() => {
        tempReportApi.findByMainReportId(id).then((response) => {
          const reports = response.data
          let statusCode = 0
          let progressData = null
          let progressDoneData = null
          let progressOrder = 0
          this.progressDataCount = reports.length || 1
          for (const r of reports) {
            if (r.statusCode === 10) {
              if (statusCode === 0) {
                statusCode = 10
                progressDoneData = r.progress
              }
              progressOrder++
            } else {
              statusCode = r.statusCode
              if (!progressData) {
                progressData = r.progress
              }
            }
          }
          if (!progressData) {
            progressData = progressDoneData
          }
          this.progressData = progressData
          this.progressDataOrder = progressOrder

          if (statusCode && (statusCode === 10 || statusCode < 0)) {
            clearInterval(this.timer)
            if (this.diagnosingId) {
              tempReportApi.seeById(this.diagnosingId)
            }

            if (statusCode === 10) {
              setTimeout(() => {
                this.$router.push({ name: 'TempReport', params: { id } })
                this.setDiagnosingId(null)
              }, 2000)
            } else {
              let errorMessage
              clearInterval(this.timer)
              if (this.diagnosingId) {
                tempReportApi.seeById(this.diagnosingId)
              }

              switch (statusCode) {
                case -10:
                case -20:
                case -40:
                  errorMessage = 'diagnostics.navigationFailed'
                  break
                case -30:
                  errorMessage = 'diagnostics.navigationExpired'
                  break
                case -50:
                  errorMessage = 'diagnostics.loginFailed'
                  break
                default:
                  errorMessage = 'diagnostics.diagnosisFailed'
              }
              modalEvent.openDialog({
                message: errorMessage,
                ok: 'common.confirm'
              }).then(() => {
                this.doCancel()
              })
            }
          } else {
            if (this.progressWhat === 'waiting') {
              tempReportApi.getQueue().then((d) => {
                this.queueCount = d.data
              })
            }
          }
        }).catch(() => {
          modalEvent.openDialog({
            message: 'diagnostics.diagnosisFailed',
            ok: 'common.confirm'
          }).then(() => {
            this.doCancel()
          })
        })
      }, 1000)
    },
    toggleDiagnosisDevice () {
      this.showDiagnosisDevice = !this.showDiagnosisDevice
    },
    setDeviceType (value) {
      this.model.deviceType = value
      this.showDiagnosisDevice = false
    },
    getDisabledStyle (value) {
      const style = {
        opacity: value ? '1' : '0.5'
      }
      return style
    },
    onUrlInput (model, fieldName) {
      let url = '' + model[fieldName]
      url = url.trim()

      if (!url.length) {
        return
      }

      if ('http://'.indexOf(url) === 0 || 'https://'.indexOf(url) === 0) {
        return
      }

      if (url.indexOf('http://') === 0 || url.indexOf('https://') === 0) {
        return
      }

      model[fieldName] = 'http://' + url
    },
    onFileGetClick () {
      if (this.diagnosingId) {
        return
      }

      const files = document.getElementById('diagnosisFile')
      const file = files.files[0]
      let reader = new FileReader()
      reader.onload = e => {
        try {
          let data = parseXlsx(reader.result)
          this.xlsxTempImageData = {}
          this.xlsxColumn = data.column
          this.xlsxData = data.data
        } catch (error) {
          modalEvent.openDialog({
            message: 'diagnostics.diagnosisFailed',
            ok: 'common.confirm'
          }).then(() => {
            this.doCancel()
          })
        }
      }
      reader.readAsBinaryString(file)
      this.fileName = file.name
    },
    setPreviewFile (e, idx) {
      if (this.xlsxTempImageData) {
        this.$set(this.xlsxTempImageData, idx, URL.createObjectURL(e.target.files[0]))
      }
    },
    onFileStartClick () {
      if (this.diagnosingId) {
        return
      }
      this.checkDiagnosing().then(() => {
        this.errors = {}
        const model = new FormData()
        const file = document.getElementById('diagnosisFile')
        model.append('diagnosisType', this.diagnosisType)
        model.append('wcagVersion', this.model.wcagVersion)
        model.append('file', file.files[0])

        for (let idx = 0; idx < this.xlsxData.length; idx++) {
          this.xlsxData[idx]['_idx'] = `${idx}`
          const image = document.getElementById(`diagnosisItem${idx}`)
          if (image) {
            const file = image.files[0]
            if (file) {
              model.append(`image${idx}`, file)
              this.xlsxData[idx]['_image'] = `image${idx}-${file.name}`
            }
          }
        }
        model.append('data', JSON.stringify(this.xlsxData))
        this.progressData = null

        this.setDiagnosingId(true)
        tempReportApi.addFile(model).then(response => {
          const tempReportId = response.data.tempReportId
          if (tempReportId) {
            this.setDiagnosing(tempReportId)
          } else {
            throw new Error('invalid tempReportId')
          }
        }).catch(err => {
          this.setDiagnosingId(null)
          this.handleAjaxError(err)
        })
      }).catch(() => {
        modalEvent.openDialog({
          message: 'diagnostics.diagnosisFailed',
          ok: 'common.confirm'
        }).then(() => {
          this.doCancel()
        })
      })
    },
    onStartClick () {
      if (this.diagnosingId) {
        return
      }
      this.checkDiagnosing().then(() => {
        this.errors = {}
        let selectorError = null
        this.setExcludeSelectors()

        if (this.excludeSelectors.length) {
          for (let i = 0; i < this.excludeSelectors.length; i++) {
            if (this.excludeSelectors[i].value.trim() === '') {
              selectorError = 'validation.emptyFields'
              break
            }
          }
        }

        this.setIncludeSelectors()

        if (this.includeSelectors.length) {
          for (let i = 0; i < this.includeSelectors.length; i++) {
            if (this.includeSelectors[i].value.trim() === '') {
              selectorError = 'validation.emptyFields'
              break
            }
          }
        }

        const isShowOptions = this.isShowOptions
        const model = Object.assign({}, this.model, this.options)
        model.diagnosisType = this.diagnosisType
        this.progressData = null

        this.setDiagnosingId(true)
        tempReportApi.add(model).then(response => {
          if (isShowOptions) {
            this.isShowOptions = false
          }

          const tempReportId = response.data.tempReportId
          if (tempReportId) {
            this.setDiagnosing(tempReportId)
          } else {
            throw new Error('invalid tempReportId')
          }
        }).catch(err => {
          this.handleAjaxError(err)

          if (this.isValidationError(err)) {
            if (selectorError) {
              this.errors.useExcludeSelectors = selectorError
              this.errors.useIncludeSelectors = selectorError
            }
          }
        })
      }).catch(() => {
        modalEvent.openDialog({
          message: 'diagnostics.diagnosisProcessing',
          ok: 'common.confirm'
        })
      })
    },
    onUrlSubmit () {
      if (this.canUrlSubmit) {
        this.onStartClick()
      }
    },
    setExcludeSelectors () {
      this.options.excludeSelectors = this.excludedSelectors.join('\n').trim()
    },
    setIncludeSelectors () {
      this.options.includeSelectors = this.includedSelectors.join('\n').trim()
    },
    onOptionsToggle () {
      if (this.isShowOptions) {
        const hasOption = this.options.useExcludeSelectors || this.options.useIncludeSelectors || this.options.requireLogin

        if (hasOption) {
          modalEvent.openDialog({
            message: 'diagnostics.closeOptions',
            ok: 'common.yes',
            cancel: 'common.no'
          }).then(result => {
            if (result) {
              this.resetModel()
              this.isShowOptions = false
            }
          })
          return
        }
      }
      this.isShowOptions = !this.isShowOptions
    },
    onExcludeSelectorAdd () {
      this.isShowExcludeSelectorsMore = true
      this.excludeSelectors.push({ value: '' })
    },
    onExcludeSelectorRemove (index) {
      this.excludeSelectors.splice(index, 1)
    },
    onIncludeSelectorAdd () {
      this.isShowIncludeSelectorsMore = true
      this.includeSelectors.push({ value: '' })
    },
    onIncludeSelectorRemove (index) {
      this.includeSelectors.splice(index, 1)
    },
    resetModel () {
      this.excludeSelectors = []
      this.includeSelectors = []
      for (let [key, value] of Object.entries(this.options)) {
        if (_.isString(value)) {
          this.options[key] = null
        } else if (_.isBoolean(value)) {
          this.options[key] = false
        }
      }
      this.selectorPreset.forEach(preset => {
        preset.selected = false
      })
    },
    onCancelClick () {
      modalEvent.openDialog({
        message: 'diagnostics.cancelDiagnosis',
        ok: 'common.confirm'
      }).then(result => {
        if (result) {
          this.doCancel()
        }
      })
    },
    doCancel () {
      clearInterval(this.timer)
      if (this.diagnosingId) {
        tempReportApi.seeById(this.diagnosingId)
      }
      this.setDiagnosingId(null)
      this.resetModel()
    },
    toggleUseExcludeSelectors () {
      const useExcludeSelectors = this.options.useExcludeSelectors

      if (useExcludeSelectors) {
      } else {
        this.selectorPreset.forEach(item => {
          item.selected = false
        })
        this.excludeSelectors = []
      }
    },
    toggleUseIncludeSelectors () {
      const useIncludeSelectors = this.options.useIncludeSelectors

      if (useIncludeSelectors) {
      } else {
        this.includeSelectors = []
      }
    },
    toggleRequireLogin () {
      const requireLogin = this.options.requireLogin

      for (const [key, value] of Object.entries(this.defaultLoginOptions)) {
        if (requireLogin) {
          if (key === 'loginUrl') {
            this.options[key] = this.defaultLoginUrl[this.loginOption]
          } else {
            this.options[key] = value
          }
        } else {
          if (_.isString(value)) {
            this.options[key] = null
          } else if (_.isBoolean(value)) {
            this.options[key] = false
          }
        }
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.new-report-form {
  box-shadow: 0 12px 38px 0 rgba(0, 0, 0, 0.3);
  background-color: #151515;
  background-size: auto 350px;
  background-position: calc(50% + 170px) -53px;
  background-repeat: no-repeat;

  &.SAMSUNG {
    background-image: url('../../assets/asset--header-samsung.jpg');
  }

  .new-report-form-wrap {
    width: 1030px;
    padding: 0 15px;
    margin: 0 auto;
  }

  .diagnosing {
    text-align: center;

    h2 {
      font-size: 36px;
      font-weight: 500;
      padding: 47px 0 20px 90px;

      button {
        font-size: 13px;
        font-weight: 400;
        width: 80px;
        color: #c1c1c1;
        border-radius: 20px;
        border: solid 1px #555555;
        padding: 7px 0;
        vertical-align: bottom;
        margin-bottom: 8px;
        margin-left: 3px;
      }
    }

    .progress-bar {
      margin: 20px auto 5px;
      width: 685px;
      border-radius: 6px;
      box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.15);
      background-color: #000000;
      height: 11px;

      .bar {
        position: relative;
        height: 11px;
        border-radius: 6px;
        background-image: linear-gradient(90deg, #ec0047 50%, #ffda22 75%);
        background-size: 150% 150%;
        animation: rocket 1s ease infinite alternate;
        transition: width 3s ease;

        .icon {
          position: absolute;
          top: calc(50% - 22px);
          right: -23.5px;
          width: 34px;
          background-size: 47px 47px;
          background-position: right top;
          transition: width 1s ease;
        }

        .bubble {
          position: absolute;
          top: -38px;
          width: 44px;
          right: -26px;
          display: block;
          font-weight: 500;
          background: #ffffff;
          border-radius: 5px;
          padding: 3px 0;
          font-size: 13px;
          text-align: center;
          color: #ec0047;
          transition: opacity 0.3s ease;
          opacity: 0;

          &:before {
            content: "";
            position: absolute;
            bottom: -4px;
            right: calc(50% - 5px);
            border-left: 5px solid transparent;
            border-right: 5px solid transparent;
            border-top: 5px solid #fff;
          }
        }

        &.on {
          .icon {
            width: 47px;
          }

          .bubble {
            opacity: 1;
          }
        }
      }
    }

    .progress-what {
      display: flex;
      margin: 0 auto;
      padding: 0 0 74px 0;
      width: 845px;

      .what {
        display: inline-block;
        text-align: center;
        color: #ffffff;
        width: 25%;
        transition: opacity 0.3s ease;

        h5 {
          font-size: 14px;
          font-weight: 600;
          margin-top: -3px;
          margin-bottom: 3px;

          span {
            opacity: 0.3;
            transition: opacity 0.3s ease,
                        color 0.3s ease;

            &.ac {
              opacity: 1;
              color: yellow;
            }
          }
        }

        p {
          font-size: 12px;
          font-weight: 400;
          color: #c1c1c1;
          transition: opacity 0.3s ease;
        }

        &:not(.active) {
          opacity: 0.2;

          p {
            opacity: 0;
          }
        }
      }
    }
  }

  h2 {
    font-size: 42px;
    font-weight: 600;
    color: #ffffff;
    padding-top: 42px;
  }

  .make-diagnosis-tab {
    ul {
      margin: 15px 0 19px 0;

      li {
        display: inline-block;
        margin-right: 40px;

        a {
          font-size: 20px;
          font-weight: 600;
          color: #888888;
          display: inline-block;
          padding: 2px 0;
          margin: 0 2px 0 4px;
          border-bottom: 3px solid transparent;

          &.active {
            color: #ec0047;
            border-bottom-color: #ec0047;
          }
        }
      }
    }
  }

  .file-options {
    padding-bottom: 70px;

    .inputs {
      white-space: nowrap;
    }

    .file-input {
      position: relative;
      display: inline-block;
      vertical-align: top;
      width: calc(100% - 130px);

      .label {
        position: absolute;
        top: 0;
        left: 0;
        width: 266px;
        border-right: 1px solid #d7d7d7;

        .current {
          position: relative;
          z-index: 2;
          height: 50px;
          line-height: 50px;
          font-weight: 600;
          color: #222222;
          padding-left: 23px;
          font-size: 14px;
          cursor: pointer;
        }
      }

      .file {
        border-radius: 25px;
        background-color: #ffffff;
        border: none;
        width: calc(100% - 274px);
        padding-left: 274px;
        height: 50px;
        line-height: 50px;
        font-size: 14px;
        cursor: pointer;
        display: inline-block;
        overflow: hidden;

        .button {
          line-height: 50px;
          font-size: 16px;
          color: #888888;

          &.has {
            color: #444444;
          }

          button {
            margin-right: 5px;
            height: 34px;
            line-height: 30px;
            padding: 0 10px;
            border-radius: 4px;
            box-shadow: 0 3px 2px -2px rgba(0, 0, 0, 0.06);
            border: solid 1px rgba(0, 0, 0, 0.3);
            background-color: #ffffff;
            font-size: 14px;
            color: #444444;
          }
        }

        input {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          cursor: pointer;
          opacity: 0;
        }
      }
    }

    .file-start {
      vertical-align: top;
      display: inline-block;
      width: 120px;
      margin-left: 5px;

      button {
        border-radius: 25px;
        background-color: #ec0047;
        color: #FFFFFF;
        text-transform: uppercase;
        width: 120px;
        padding: 0;
        font-size: 20px;
        text-align: center;
        height: 50px;
        line-height: 50px;
        font-weight: 600;
        transition: background-color 0.15s ease,
        color 0.15s ease;

        &:disabled {
          background-color: #666666;
          color: #999999;
          cursor: not-allowed;
        }
      }
    }

    .file-template {
      text-align: right;
      padding-right: 140px;
      font-size: 13px;
      height: 0;
      overflow: visible;

      a {
        color: #ec0047;
      }
    }

    .file-items {
      padding-top: 50px;
      color: #ffffff;

      .file-start {
        display: block;
        width: auto;
        text-align: right;
        margin-top: 20px;
        margin-bottom: -35px;
      }

      .file-data {
        th {
          font-size: 12px;
          font-weight: 500;
          color: #d7d7d7;
          border-bottom: 2px solid #aaaaaa;
          padding: 2px 0 2px 0;
          text-align: center;
        }

        td {
          padding: 7px 0;
          font-size: 13px;
          vertical-align: top;
          color: #c1c1c1;
          word-break: break-all;
          text-align: center;
          border-bottom: 1px solid #aaaaaa;
          transition: background-color 0.15s ease;
        }

        .file-item-idx {
          width: 70px;
          padding: 18px 0;
          text-align: center;
        }

        .file-item-image {
          width: 210px;
          padding: 10px;

          .file-item-wrap {
            border: solid 1px #444444;
            background-color: rgba(255, 255, 255, 0.08);
            width: 107px;
            height: 85px;
            margin: 5px auto;
            box-sizing: border-box;
            padding-top: 26px;
            position: relative;

            &.has {
              width: auto;
              height: auto;
              padding-top: 4px;
            }

            button {
              height: 30px;
              line-height: 28px;
              padding: 0 10px;
              border-radius: 4px;
              box-shadow: 0 3px 2px -2px rgba(0, 0, 0, 0.06);
              border: solid 1px rgba(0, 0, 0, 0.3);
              background-color: #ffffff;
              font-size: 13px;
              color: #444444;
              margin-bottom: 13px;
            }

            input {
              position: absolute;
              top: 0;
              left: 0;
              width: 100%;
              height: 100%;
              opacity: 0;
              cursor: pointer;
            }

            img {
              max-width: 198px;
              max-height: 100%;
              margin-bottom: 5px;
              border: 1px solid rgba(255, 255, 255, 0.3);
              border-radius: 4px;
            }
          }
        }

        .file-item-attr {
          margin-left: 20px;
          vertical-align: top;
          padding: 0;

          table {
            th {
              font-size: 14px;
              padding: 12px 40px 12px 5px;
              width: 155px;
              font-weight: bold;
              vertical-align: top;
              text-align: right;
              border-bottom: none;
              color: #b1b1b1;
              line-height: 1.29;
            }

            td {
              font-size: 14px;
              padding: 12px 10px 12px 6px;
              border-top: 1px solid #444444;
              font-weight: 300;
              vertical-align: top;
              text-align: left;
              border-bottom: none;
              word-break: keep-all;
              line-height: 1.29;
              color: #d7d7d7;
            }

            tr:first-child {
              td {
                border-top: none;
              }
            }
          }
        }
      }
    }
  }

  .url-options {
    white-space: nowrap;

    .url-input {
      position: relative;
      display: inline-block;
      vertical-align: top;
      width: calc(100% - 130px);

      .url {
        input {
          border-radius: 25px;
          background-color: #ffffff;
          color: #000000;
          border: none;
          width: 100%;
          padding-left: 20px;
          height: 50px;
          line-height: 50px;
          font-size: 18px;
        }
      }
    }

    .url-start {
      vertical-align: top;
      display: inline-block;
      width: 120px;
      margin-left: 5px;

      button {
        border-radius: 25px;
        background-color: #ec0047;
        color: #FFFFFF;
        text-transform: uppercase;
        width: 120px;
        padding: 0;
        font-size: 20px;
        text-align: center;
        height: 50px;
        line-height: 50px;
        font-weight: 600;
        transition: background-color 0.15s ease,
                    color 0.15s ease;

        &:disabled {
          background-color: #666666;
          color: #999999;
          cursor: not-allowed;
        }
      }
    }
  }

  .toggle-advanced {
    margin-top: 25px;
    text-align: center;

    button {
      position: relative;
      font-size: 14px;
      font-weight: 500;
      color: #ffffff;
      padding-bottom: 22px;

      span {
        position: absolute;
        font-size: 17px;
        bottom: 0;
        left: 50%;
        transform: translateX(-50%) scaleY(0.6);
        color: #ec0047;
        transition: transform 0.3s ease;
      }
    }

    &.on {
      button span {
        transform: translateX(-50%) scaleY(0.6) rotate(180deg);
      }
    }
  }

  .advanced-options {
    max-height: 1000px;

    .option-group {
      position: relative;
      padding-left: 33px;
      margin-top: 55px;
    }

    .option-group:nth-child(2) {
      margin-top: 65px;
    }

    .option-title {
      .input-check {
        position: absolute;
        top: 8px;
        left: 5px;
      }

      .input-label {
        font-size: 18px;
        font-weight: 600;
        color: #ffffff;
        display: block;
      }

      .input-error {
        margin: 9px 0;
        display: block;
        font-size: 13px;
        color: #ec0047;
      }

      .input-info {
        margin: 9px 0;
        display: block;
        font-weight: 300;
        font-size: 13px;
        color: #b1b1b1;
      }
    }

    .sub-options {
      .exclude-list {
        margin: 20px 0;

        li {
          display: inline-block;
          font-size: 16px;
          color: #ffffff;
          margin-left: 1px;
          margin-right: 40px;

          input {
            margin-right: 8px;
          }
        }
      }

      .exclude-more {
        li {
          margin: 10px 20px 10px 0;
          display: inline-block;

          button {
            padding: 5px;
            font-size: 14px;
            color: #999999;

            &:hover {
              color: #ffffff;
            }
          }
        }
      }

      .excluded-list {
        background: rgba(255, 255, 255, 0.1);
        border-radius: 10px;
        color: #999999;
        padding: 4px 2px 7px;
        margin-bottom: 15px;

        li {
          margin: 0 5px;
          display: inline-block;
          font-family: monospace;
          padding: 0 5px;
          border-radius: 5px;
          transition: background 0.2s ease,
                      color 0.2s ease;

          &:hover {
            background: rgba(255, 255, 255, 0.2);
            color: #ffffff;
          }
        }
      }

      .add-btn {
        border-radius: 20px;
        background-color: #ffffff;
        padding: 9px 15px 9px 11px;
        font-weight: 500;
        line-height: 16px;
        color: #222222;

        .icon {
          margin-right: 5px;
        }
      }

      .target {
        margin: 20px 0;

        li {
          display: inline-block;
          font-size: 16px;
          color: #ffffff;
          margin-right: 45px;

          input {
            margin-right: 8px;
          }
        }
      }

      .login-options {
        margin: -5px 0;

        li {
          position: relative;
          display: inline-block;
          padding-left: 107px;
          padding-right: 76px;
          margin: 18px 0 17px 0;

          label {
            position: absolute;
            top: 50%;
            left: 0;
            width: 97px;
            transform: translateY(-50%);
            font-size: 14px;
            line-height: 1.2;
            color: #888888;
          }

          input {
            width: 280px;
          }
        }
      }
    }
  }

  input[type=text],
  input[type=password] {
    padding: 12px;
    border-radius: 6px;
    border: solid 1px #ebebeb;
    background-color: #0d0d0d;
    width: 180px;
    height: 38px;
    line-height: 38px;
    transition: border-color 0.2s ease;
    color: #ffffff;

    &:focus {
      border-color: #289eef;
    }

    &.error {
      border-color: #ec0047;
    }

    & + span.query-selector-input {
      position: relative;
      top: 0;
      right: -15px;

      &::before {
        content: "Enter Query Selector";
        display: block;
        position: absolute;
        top: -32px;
        right: 0;
        background: #ffffff;
        color: #444444;
        border-radius: 4px;
        padding: 4px 8px;
        font-size: 13px;
        transform: translateY(10px);
        opacity: 0;
        transition: transform 0.15s ease,
                    opacity 0.15s ease;
        white-space: nowrap;
      }

      &::after {
        content: "";
        display: block;
        position: absolute;
        top: -7px;
        right: 25px;
        border-top: 10px solid #ffffff;
        border-left: 10px solid transparent;
        border-right: 10px solid transparent;
        transform: translateY(10px);
        opacity: 0;
        transition: transform 0.15s ease,
                    opacity 0.15s ease;
      }
    }

    &:not(:disabled):focus + span.query-selector-input,
    &:not(:disabled):hover + span.query-selector-input {
      &::before,
      &::after {
        transform: translateY(0);
        opacity: 1;
      }
    }
  }
}

@keyframes rocket {
  from {
    background-position: 0 0;
  }

  to {
    background-position: 50% 0;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/valid-member/NewReportForm.vue